import React, { useCallback, useMemo, useState } from 'react';

import { useSnackbar } from 'notistack';
import qs from 'qs';

import { Box, Button, Link, Stack, TextField } from '@mui/material';
import {
  DataGridPro,
  GridColDef,
  GridPaginationModel,
  GridRenderCellParams,
  GridSortModel,
} from '@mui/x-data-grid-pro';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import type { TagType } from '~/consts/tags';
import { TAG_TYPE_CAMPAIGN, TAG_TYPE_HOLIDAY } from '~/consts/tags';

import { customerPortalHost } from '~/services/common';

const DEFAULT_PAGE_SIZE = 25;
const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];
const ROW_ACTION_BUTTON_SX = { minWidth: 64, px: 1.5 };
const MIN_TAG_NAME_LENGTH = 3;
const MAX_TAG_NAME_LENGTH = 64;

function validateTagName(name: string): string | null {
  const trimmedName = name.trim();

  if (!trimmedName) {
    return 'Tag name is required.';
  }

  if (trimmedName.length < MIN_TAG_NAME_LENGTH) {
    return `Tag name must be at least ${MIN_TAG_NAME_LENGTH} characters.`;
  }

  if (trimmedName.length > MAX_TAG_NAME_LENGTH) {
    return `Tag name must be ${MAX_TAG_NAME_LENGTH} characters or fewer.`;
  }

  if (trimmedName.includes(',')) {
    return 'Tag name cannot contain a comma.';
  }

  return null;
}

function getCustomerPortalUrl(type: TagType, tagName: string) {
  const basePath = `${customerPortalHost({ value: 'lux' })}?`;

  switch (type) {
    case TAG_TYPE_HOLIDAY:
      return `${basePath}${qs.stringify({ holidayTypes: tagName })}`;
    case TAG_TYPE_CAMPAIGN:
    default:
      return `${basePath}${qs.stringify({ [type]: tagName })}`;
  }
}

type Props = {
  tags: Array<App.MarketingTag>;
  type: TagType;
  onDeleteTag: (type: TagType, id: number) => Promise<void>;
  onUpdateTag: (type: TagType, id: number, name: string) => Promise<void>;
};

export default function TagsDataGrid({ tags, type, onDeleteTag, onUpdateTag }: Props) {
  const { enqueueSnackbar } = useSnackbar();
  const showConfirmDialog = useConfirmDialog();

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'name', sort: 'asc' }]);
  const [editingTagId, setEditingTagId] = useState<number | null>(null);
  const [editedName, setEditedName] = useState('');
  const [editNameError, setEditNameError] = useState<string | null>(null);
  const [processingTagId, setProcessingTagId] = useState<number | null>(null);

  const sortedRows = useMemo(() => {
    const sort = sortModel[0];
    if (!sort) {
      return tags;
    }

    const direction = sort.sort === 'desc' ? -1 : 1;

    return [...tags].sort((rowA, rowB) => rowA.name.localeCompare(rowB.name) * direction);
  }, [tags, sortModel]);

  const handleDelete = useCallback(
    async (tag: App.MarketingTag) => {
      const confirmed = await showConfirmDialog({
        title: 'Delete tag',
        description: 'This tag may be attached to live offers, are you sure you want to delete?',
      });

      if (!confirmed) {
        return;
      }

      try {
        setProcessingTagId(tag.id);
        await onDeleteTag(type, tag.id);
      } catch {
        enqueueSnackbar("Server error. Can't delete tag.", { variant: 'error' });
      } finally {
        setProcessingTagId(null);
      }
    },
    [enqueueSnackbar, onDeleteTag, showConfirmDialog, type],
  );

  const handleSave = useCallback(
    async (tagId: number) => {
      const validationError = validateTagName(editedName);
      if (validationError) {
        setEditNameError(validationError);
        enqueueSnackbar(validationError, { variant: 'error' });
        return;
      }

      setEditNameError(null);
      setProcessingTagId(tagId);

      try {
        await onUpdateTag(type, tagId, editedName.trim());
        setEditingTagId(null);
        enqueueSnackbar('Tag changes has been saved', { variant: 'success' });
      } catch {
        enqueueSnackbar("Server error. Can't save tag.", { variant: 'error' });
      } finally {
        setProcessingTagId(null);
      }
    },
    [editedName, enqueueSnackbar, onUpdateTag, type],
  );

  const columns = useMemo<Array<GridColDef<App.MarketingTag>>>(
    () => [
      {
        field: 'name',
        headerName: 'Tag name',
        flex: 1,
        minWidth: 220,
        renderCell: ({ row }: GridRenderCellParams<App.MarketingTag>) => {
          if (editingTagId === row.id) {
            return (
              <TextField
                size="small"
                type="text"
                label="Tag name"
                value={editedName}
                onChange={(event) => {
                  setEditedName(event.target.value);
                  if (editNameError) {
                    setEditNameError(null);
                  }
                }}
                inputProps={{
                  minLength: MIN_TAG_NAME_LENGTH,
                  maxLength: MAX_TAG_NAME_LENGTH,
                }}
                required
                fullWidth
                error={!!editNameError}
                helperText={editNameError ?? ' '}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    void handleSave(row.id);
                  }
                }}
              />
            );
          }

          return (
            <Link href={getCustomerPortalUrl(type, row.name)} target="_blank" rel="noreferrer" underline="hover">
              {row.name}
            </Link>
          );
        },
      },
      {
        field: 'actions',
        headerName: 'Actions',
        width: 200,
        sortable: false,
        filterable: false,
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ row }: GridRenderCellParams<App.MarketingTag>) => {
          const isProcessing = processingTagId === row.id;

          return (
            <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" width="100%" pr={1}>
              {editingTagId === row.id ? (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={ROW_ACTION_BUTTON_SX}
                    disabled={isProcessing}
                    onClick={() => handleSave(row.id)}
                  >
                    {isProcessing ? 'Saving' : 'Save'}
                  </Button>
                  <Button
                    variant="text"
                    size="small"
                    sx={ROW_ACTION_BUTTON_SX}
                    disabled={isProcessing}
                    onClick={() => {
                      setEditingTagId(null);
                      setEditedName(row.name);
                      setEditNameError(null);
                    }}
                  >
                    Cancel
                  </Button>
                </>
              ) : (
                <>
                  <Button
                    variant="outlined"
                    size="small"
                    sx={ROW_ACTION_BUTTON_SX}
                    disabled={isProcessing}
                    onClick={() => {
                      setEditingTagId(row.id);
                      setEditedName(row.name);
                      setEditNameError(null);
                    }}
                  >
                    Edit
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    color="error"
                    sx={ROW_ACTION_BUTTON_SX}
                    disabled={isProcessing}
                    onClick={() => handleDelete(row)}
                  >
                    Delete
                  </Button>
                </>
              )}
            </Stack>
          );
        },
      },
    ],
    [editedName, editNameError, editingTagId, handleDelete, handleSave, processingTagId, type],
  );

  if (!tags.length) {
    return null;
  }

  return (
    <Box sx={{ width: '100%' }}>
      <DataGridPro
        rows={sortedRows}
        columns={columns}
        getRowId={(row) => row.id}
        autoHeight
        density="compact"
        disableRowSelectionOnClick
        pagination
        paginationMode="client"
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={PAGE_SIZE_OPTIONS}
        sortingMode="client"
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        sx={{
          border: 1,
          borderColor: 'divider',
          '& .MuiDataGrid-columnHeaders': {
            backgroundColor: 'grey.50',
          },
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: 0.75,
          },
          '& .MuiDataGrid-cell[data-field="actions"], & .MuiDataGrid-columnHeader[data-field="actions"]': {
            pr: 2,
          },
        }}
      />
    </Box>
  );
}
